
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import { ifProp } from 'styled-tools';
import IconButton from 'src/components/core/buttons/icon-button';
import Label from 'src/components/core/typography/label';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import arrowLeftIcon from 'src/assets/svg/arrow-left-2.svg';
import arrowRightIcon from 'src/assets/svg/arrow-right-2.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  current?: number,
  displayPagination?: boolean,
  onGoToNext: () => void,
  onGoToPrevious: () => void,
  total: number
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  align-items: center;
  display: flex;
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)<{ disabled: boolean }>`
  color: ${color('grey900')} !important;
  line-height: 0;

  &:first-child {
    margin-right: ${units(2)};
  }

  ${ifProp('disabled', css`
    color: ${color('grey600')} !important;
    cursor: default;
    pointer-events: none;
  `)}
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label).attrs({
  as: 'span',
  size: 'small'
})`
  color: ${color('grey900')};
  font-style: italic;
  margin-right: ${units(2)};
  min-width: 30px;
`;

/**
 * `GalleryNavigation` component.
 */

const GalleryNavigation = (props: Props): ReactElement => {
  const {
    className,
    current,
    displayPagination,
    onGoToNext,
    onGoToPrevious,
    total
  } = props;

  const [active, setActive] = useState<number>();
  const handleGoToPrevious = useCallback(() => {
    onGoToPrevious();
    setActive(active - 1);
  }, [active, onGoToPrevious]);

  const handleGoToNext = useCallback(() => {
    onGoToNext();
    setActive(active + 1);
  }, [active, onGoToNext]);

  useEffect(() => {
    setActive(current ?? 0);
  }, [current]);

  return (
    <Wrapper className={className}>
      <StyledIconButton
        aria-label={'Previous slide'}
        disabled={active === 0}
        icon={arrowLeftIcon}
        iconSize={units(3)}
        onClick={handleGoToPrevious}
      />

      {displayPagination && (
        <StyledLabel>
          {`${active + 1} / ${total}`}
        </StyledLabel>
      )}

      <StyledIconButton
        aria-label={'Next slide'}
        disabled={active === total - 1}
        icon={arrowRightIcon}
        iconSize={units(3)}
        onClick={handleGoToNext}
      />
    </Wrapper>
  );
};

/**
 * Export `GalleryNavigation` component.
 */

export default GalleryNavigation;
